import { Plus, X } from "lucide-react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useGetLandUsage } from "../../../../../api/static-data";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../../components/ui/common/table";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { getLastTwentyYears } from "../../../../../utils/utils";
import { LandUsageChange } from "../../../../../types/prodcut-data-review/product-data.types";

interface LandUseChangeRowFormValues {
  landUseYearOfChange: string;
  landUsageAllocation: string;
  landUsageFrom: string;
  landUsageTo: string;
}

interface LandUseChangeRowProps {
  landUsageChange: LandUsageChange;
  landUsageOptions: any[];
  editing: boolean;
  onUpdate: (id: string | undefined, patch: Partial<LandUsageChange>) => void;
  onRemove: (id: string | undefined) => void;
}

const LandUseChangeRow: React.FC<LandUseChangeRowProps> = ({
  landUsageChange,
  landUsageOptions,
  editing,
  onUpdate,
  onRemove
}) => {
  const form = useForm<LandUseChangeRowFormValues>({
    defaultValues: {
      landUseYearOfChange: String(landUsageChange.landUseYearOfChange ?? ""),
      landUsageAllocation:
        landUsageChange.landUsageAllocation === "" ? "" : String(landUsageChange.landUsageAllocation),
      landUsageFrom: String(landUsageChange.landUsageFrom ?? ""),
      landUsageTo: String(landUsageChange.landUsageTo ?? "")
    }
  });

  useEffect(() => {
    const subscription = form.watch(data => {
      onUpdate(landUsageChange.id, {
        landUseYearOfChange: data.landUseYearOfChange,
        landUsageAllocation: data.landUsageAllocation === "" ? "" : Number(data.landUsageAllocation),
        landUsageFrom: data.landUsageFrom,
        landUsageTo: data.landUsageTo
      });
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <Form {...form}>
      <TableRow>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormSelect
              control={form.control}
              name="landUseYearOfChange"
              label=""
              placeholder="Select year"
              options={getLastTwentyYears()}
            />
          ) : (
            <span className="tw-text-sm">{landUsageChange.landUseYearOfChange || "—"}</span>
          )}
        </TableCell>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormInputNumber control={form.control} name="landUsageAllocation" label="" />
          ) : (
            <span className="tw-text-sm tw-font-mono">
              {landUsageChange.landUsageAllocation !== "" ? landUsageChange.landUsageAllocation : "—"}
            </span>
          )}
        </TableCell>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormSelect
              control={form.control}
              name="landUsageFrom"
              label=""
              placeholder="Select"
              options={landUsageOptions}
            />
          ) : (
            <span className="tw-text-sm">{landUsageChange.landUsageFrom || "—"}</span>
          )}
        </TableCell>
        <TableCell className="[&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
          {editing ? (
            <FormSelect
              control={form.control}
              name="landUsageTo"
              label=""
              placeholder="Select"
              options={landUsageOptions}
            />
          ) : (
            <span className="tw-text-sm">{landUsageChange.landUsageTo || "—"}</span>
          )}
        </TableCell>
        <TableCell className="tw-text-right">
          {editing && (
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-7 tw-w-7"
              onClick={() => onRemove(landUsageChange.id)}
              title="Remove"
            >
              <X className="tw-h-3.5 tw-w-3.5" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    </Form>
  );
};

interface LandUseChangeSectionProps {
  landUsage: LandUsageChange[];
  editing: boolean;
  subSectionHeader: string;
  onAdd: () => void;
  onUpdate: (id: string | undefined, patch: Partial<LandUsageChange>) => void;
  onRemove: (id: string | undefined) => void;
}

const LandUseChangeSection: React.FC<LandUseChangeSectionProps> = ({
  landUsage,
  editing,
  subSectionHeader,
  onAdd,
  onUpdate,
  onRemove
}) => {
  const { data: landUsageOptions = [] } = useGetLandUsage();

  return (
    <div className="tw-border-t">
      <div className={subSectionHeader}>
        <span className="tw-text-xs tw-font-semibold tw-text-primary tw-uppercase tw-tracking-wide">
          Land Use Change
        </span>
        {editing && (
          <Button variant="outline" size="sm" className="tw-h-7 tw-text-xs" onClick={onAdd}>
            <Plus className="tw-h-3 tw-w-3 tw-mr-1" />
            Add land use change
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw-text-xs">Year of Change</TableHead>
            <TableHead className="tw-text-xs">Allocation (%)</TableHead>
            <TableHead className="tw-text-xs">From</TableHead>
            <TableHead className="tw-text-xs">To</TableHead>
            <TableHead className="tw-text-xs tw-w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {landUsage.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="tw-text-xs tw-text-muted-foreground tw-italic">
                No land use change data available.
              </TableCell>
            </TableRow>
          )}
          {landUsage.map((l, idx) => (
            <LandUseChangeRow
              key={l.id ?? idx}
              landUsageChange={l}
              landUsageOptions={landUsageOptions}
              editing={editing}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default LandUseChangeSection;
